import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Target,
  BarChart3,
  Loader2
} from 'lucide-react';
import NavBar from '../components/NavBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  'digital_strategy',
  'technology_infrastructure',
  'data_analytics_ai',
  'process_automation',
  'customer_experience',
  'organization_culture'
];

const AssessmentDetailPage = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [assessment, setAssessment] = useState(null);
  const [progress, setProgress] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [activePillars, setActivePillars] = useState([]); // Pillars with questions
  const [currentPillarIndex, setCurrentPillarIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  
  useEffect(() => {
    fetchAssessmentData();
  }, [id]);
  
  const fetchAssessmentData = async () => {
    try {
      const [assessmentRes, progressRes, questionsRes] = await Promise.all([
        axios.get(`${API}/assessments/${id}`),
        axios.get(`${API}/assessments/${id}/progress`),
        axios.get(`${API}/assessments/${id}/questions`)
      ]);
      
      setAssessment(assessmentRes.data);
      setProgress(progressRes.data);
      setQuestions(questionsRes.data);
      
      // Find pillars that have questions
      const pillarsWithQuestions = PILLARS.filter(pillar => 
        questionsRes.data.some(q => q.pillar === pillar)
      );
      setActivePillars(pillarsWithQuestions);
      
      // Initialize answers from existing responses
      const existingAnswers = {};
      questionsRes.data.forEach(q => {
        if (q.answer) {
          existingAnswers[q.id] = q.answer;
        }
      });
      setAnswers(existingAnswers);
      
      // Check if already completed
      if (assessmentRes.data.status === 'completed') {
        setCompleted(true);
        setResult({
          gdmii_score: assessmentRes.data.gdmii_score
        });
      }
    } catch (error) {
      console.error('Error fetching assessment:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const currentPillar = activePillars[currentPillarIndex] || PILLARS[0];
  const pillarQuestions = questions.filter(q => q.pillar === currentPillar);
  const currentQuestion = pillarQuestions[currentQuestionIndex];
  
  const handleAnswer = async (value) => {
    if (!currentQuestion) return;
    const newAnswers = { ...answers, [currentQuestion.id]: parseInt(value) };
    setAnswers(newAnswers);
    
    // Save answer to backend
    try {
      await axios.post(`${API}/responses`, {
        assessment_id: id,
        question_id: currentQuestion.id,
        answer: parseInt(value)
      });
    } catch (error) {
      console.error('Error saving response:', error);
    }
  };
  
  const goToNext = () => {
    if (currentQuestionIndex < pillarQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (currentPillarIndex < activePillars.length - 1) {
      setCurrentPillarIndex(currentPillarIndex + 1);
      setCurrentQuestionIndex(0);
    }
  };
  
  const goToPrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (currentPillarIndex > 0) {
      setCurrentPillarIndex(currentPillarIndex - 1);
      const prevPillarQuestions = questions.filter(q => q.pillar === activePillars[currentPillarIndex - 1]);
      setCurrentQuestionIndex(Math.max(0, prevPillarQuestions.length - 1));
    }
  };
  
  const [error, setError] = useState(null);
  
  const handleComplete = async () => {
    setCompleting(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/assessments/${id}/complete`, {}, {
        timeout: 60000 // 60 second timeout
      });
      setResult(response.data);
      setCompleted(true);
    } catch (error) {
      console.error('Error completing assessment:', error);
      setError(language === 'tr' 
        ? 'Değerlendirme tamamlanırken bir hata oluştu. Lütfen tekrar deneyin.' 
        : 'Error completing assessment. Please try again.');
    } finally {
      setCompleting(false);
    }
  };
  
  const isLastQuestion = currentPillarIndex === activePillars.length - 1 && 
                         currentQuestionIndex === pillarQuestions.length - 1;
  
  const totalAnswered = Object.keys(answers).length;
  const progressPercentage = (totalAnswered / questions.length) * 100;
  const allQuestionsAnswered = totalAnswered >= questions.length;
  const remainingQuestions = questions.length - totalAnswered;
  
  const getPillarName = (pillar) => {
    const names = {
      digital_strategy: { tr: 'Dijital Strateji & Liderlik', en: 'Digital Strategy & Leadership' },
      technology_infrastructure: { tr: 'Teknoloji Altyapısı', en: 'Technology Infrastructure' },
      data_analytics_ai: { tr: 'Veri, Analitik & Yapay Zeka', en: 'Data, Analytics & AI' },
      process_automation: { tr: 'Süreç Otomasyonu', en: 'Process Automation' },
      customer_experience: { tr: 'Müşteri Deneyimi', en: 'Customer Experience' },
      organization_culture: { tr: 'Organizasyon & Kültür', en: 'Organization & Culture' }
    };
    return names[pillar]?.[language] || pillar;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavBar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }
  
  // Completed view
  if (completed && result) {
    return (
      <div className="min-h-screen bg-slate-950" data-testid="assessment-completed">
        <NavBar />
        
        <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-16 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-emerald-400" />
              </div>
              
              <h1 className="text-3xl font-bold text-white mb-4">
                {language === 'tr' ? 'Değerlendirme Tamamlandı!' : 'Assessment Completed!'}
              </h1>
              
              <div className="my-8">
                <p className="text-slate-400 mb-2">
                  {language === 'tr' ? 'GDMII Skorunuz' : 'Your GDMII Score'}
                </p>
                <div className="text-6xl font-bold text-transparent bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text">
                  {result.gdmii_score?.toFixed(1)}
                </div>
                <p className="text-slate-500 mt-2">/100</p>
              </div>
              
              {result.benchmark_position && (
                <p className="text-lg text-slate-400 mb-8">
                  {language === 'tr' ? 'Benchmark Pozisyonu' : 'Benchmark Position'}: 
                  <span className="text-blue-400 font-semibold ml-2">
                    {result.benchmark_position}
                  </span>
                </p>
              )}
              
              <div className="flex justify-center space-x-4">
                <Button
                  onClick={() => navigate(`/reports/generate/${id}`)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="generate-report-btn"
                >
                  <BarChart3 className="mr-2 h-4 w-4" />
                  {t('generateReport')}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  {t('dashboard')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950" data-testid="assessment-detail">
      <NavBar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Progress Header */}
        <Card className="bg-slate-900/50 border-slate-800 mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-white">{assessment?.company?.name}</h2>
                <p className="text-sm text-slate-400">
                  {getPillarName(currentPillar)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-400">
                  {totalAnswered} / {questions.length} {language === 'tr' ? 'soru' : 'questions'}
                </p>
                <p className="text-lg font-semibold text-white">
                  {progressPercentage.toFixed(0)}%
                </p>
              </div>
            </div>
            <Progress value={progressPercentage} className="h-2 bg-slate-800" />
            
            {/* Pillar indicators */}
            <div className="flex justify-between mt-4">
              {activePillars.map((pillar, index) => {
                const pillarQs = questions.filter(q => q.pillar === pillar);
                const pillarAnswered = pillarQs.filter(q => answers[q.id]).length;
                const isComplete = pillarAnswered === pillarQs.length && pillarQs.length > 0;
                const isCurrent = index === currentPillarIndex;
                
                return (
                  <button 
                    key={pillar}
                    onClick={() => {
                      setCurrentPillarIndex(index);
                      setCurrentQuestionIndex(0);
                    }}
                    className={`flex-1 h-2 mx-0.5 rounded-full transition-all cursor-pointer hover:opacity-80 ${
                      isComplete ? 'bg-emerald-500' : 
                      isCurrent ? 'bg-blue-500' : 'bg-slate-700'
                    }`}
                    title={`${getPillarName(pillar)} - ${pillarAnswered}/${pillarQs.length}`}
                  />
                );
              })}
            </div>
            
            {/* Pillar names row */}
            <div className="flex justify-between mt-2 text-xs text-slate-500">
              {activePillars.map((pillar, index) => {
                const pillarQs = questions.filter(q => q.pillar === pillar);
                const pillarAnswered = pillarQs.filter(q => answers[q.id]).length;
                const isCurrent = index === currentPillarIndex;
                
                return (
                  <button
                    key={pillar}
                    onClick={() => {
                      setCurrentPillarIndex(index);
                      setCurrentQuestionIndex(0);
                    }}
                    className={`flex-1 text-center truncate px-1 cursor-pointer hover:text-slate-300 ${
                      isCurrent ? 'text-blue-400 font-medium' : ''
                    }`}
                  >
                    {pillarAnswered}/{pillarQs.length}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        {/* Question Card */}
        {currentQuestion && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {language === 'tr' ? 'Soru' : 'Question'} {currentQuestionIndex + 1} / {pillarQuestions.length}
                </span>
                <span className="px-2 py-1 text-xs rounded-full bg-blue-500/10 text-blue-400">
                  {currentQuestion.sub_category}
                </span>
              </div>
              <CardTitle className="text-xl text-white mt-4">
                {language === 'tr' ? currentQuestion.question_text_tr : currentQuestion.question_text_en}
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <RadioGroup
                value={answers[currentQuestion.id]?.toString() || ''}
                onValueChange={handleAnswer}
                className="space-y-3"
              >
                {currentQuestion.options?.map((option) => (
                  <div
                    key={option.value}
                    className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                      answers[currentQuestion.id] === option.value
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800/50'
                    }`}
                  >
                    <RadioGroupItem 
                      value={option.value.toString()} 
                      id={`option-${option.value}`}
                      className="border-slate-600 text-blue-500"
                    />
                    <Label 
                      htmlFor={`option-${option.value}`}
                      className="flex-1 cursor-pointer text-white"
                    >
                      {language === 'tr' ? option.label_tr : option.label_en}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              
              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-800">
                <Button
                  variant="outline"
                  onClick={goToPrevious}
                  disabled={currentPillarIndex === 0 && currentQuestionIndex === 0}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="prev-question-btn"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('previousQuestion')}
                </Button>
                
                <div className="flex flex-col items-end">
                  {error && (
                    <p className="text-red-400 text-sm mb-2">{error}</p>
                  )}
                  
                  {/* Show remaining questions warning */}
                  {!allQuestionsAnswered && isLastQuestion && (
                    <p className="text-amber-400 text-sm mb-2">
                      {language === 'tr' 
                        ? `${remainingQuestions} soru daha cevaplanmalı`
                        : `${remainingQuestions} more questions to answer`}
                    </p>
                  )}
                  
                  {isLastQuestion ? (
                    <div className="flex flex-col space-y-2">
                      <Button
                        onClick={handleComplete}
                        disabled={completing || !allQuestionsAnswered}
                        className={allQuestionsAnswered 
                          ? "bg-emerald-600 hover:bg-emerald-700" 
                          : "bg-slate-600 cursor-not-allowed"}
                        data-testid="complete-assessment-btn"
                      >
                        {completing ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {language === 'tr' ? 'Hesaplanıyor...' : 'Calculating...'}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            {t('submitAssessment')}
                          </>
                        )}
                      </Button>
                      
                      {/* Partial completion option - if at least 50% answered */}
                      {!allQuestionsAnswered && progressPercentage >= 50 && (
                        <Button
                          onClick={handleComplete}
                          disabled={completing}
                          variant="outline"
                          className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
                          data-testid="partial-complete-btn"
                        >
                          {language === 'tr' 
                            ? `Kısmi Değerlendirme (%${progressPercentage.toFixed(0)})`
                            : `Partial Assessment (${progressPercentage.toFixed(0)}%)`}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      onClick={goToNext}
                      disabled={!answers[currentQuestion.id]}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="next-question-btn"
                    >
                      {t('nextQuestion')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AssessmentDetailPage;
